/**
 * Fluid (Non-Newtonian) 3D Mesh Visualizer
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class FluidVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'fluid',
    name: 'Non-Newtonian Fluid',
    description: '3D mesh surface with wave interference',
    category: '3d',
    defaultZoom: 0.15,
    supports3D: true,
    supportsAutoRotation: true,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): number | void {
    const {
      ctx, width, height, activeWaves, speed, time, deltaTime,
      zoom, pan, isAutoRotating, autoRotRef = 0, uploadedAudioData, isUploadedAudioPlaying
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Clear with dark background
    ctx.fillStyle = '#0b0f19';
    ctx.fillRect(0, 0, width, height);

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) return autoRotRef;

    // 3D projection parameters
    const gridSize = 63;
    const gridSpacing = (width * 4.0) / gridSize;
    const offset = (gridSize * gridSpacing) / 2;
    const cameraZ = 2500 / Math.max(0.1, zoom);
    const screenScale = 1800;

    // Auto rotation
    let newAutoRot = autoRotRef;
    if (isAutoRotating) {
      newAutoRot += deltaTime * 0.2;
    }

    const rotX = 0.8 + (pan.y * 0.005);
    const rotY = (pan.x * 0.005) + newAutoRot;

    // Pre-calculate wave data
    const waveParams = displayWaves.map(w => ({
      amp: w.amp,
      spatialFreq: (w.freq * Math.PI) / (gridSize / 2),
      angleBase: (w.phase * Math.PI / 180) - (time * w.freq * 8)
    }));

    // Calculate grid points
    const points: { x: number, y: number, z: number, depth: number }[] = [];

    for (let z = 0; z < gridSize; z++) {
      for (let x = 0; x < gridSize; x++) {
        const px = (x * gridSpacing) - offset;
        const pz = (z * gridSpacing) - offset;

        // Calculate height (interference)
        let h = 0;
        if (displayWaves.length > 0) {
          let reSum = 0;
          let imSum = 0;

          const nx = ((x / gridSize) * 6) - 3;
          const nz = ((z / gridSize) * 6) - 3;

          for (const w of waveParams) {
            const d = Math.sqrt(nx * nx + nz * nz);
            const spatialVal = Math.cos(w.spatialFreq * d * 5) + Math.cos(w.spatialFreq * nx * 2);

            reSum += w.amp * spatialVal * Math.cos(w.angleBase);
            imSum += w.amp * spatialVal * Math.sin(w.angleBase);
          }

          const mag = Math.sqrt(reSum * reSum + imSum * imSum);
          const threshold = 15;
          if (mag > threshold) {
            h = -Math.pow((mag - threshold), 1.2) * 1.5;
          } else {
            h = -Math.sin(mag) * 2;
          }
        }

        // 3D transformation
        const rx = px * Math.cos(rotY) - pz * Math.sin(rotY);
        let rz = px * Math.sin(rotY) + pz * Math.cos(rotY);
        const ry = h * Math.cos(rotX) - rz * Math.sin(rotX);
        rz = h * Math.sin(rotX) + rz * Math.cos(rotX);

        const Z = rz + cameraZ;
        if (Z <= 100) continue;

        const xPrime = rx / Z;
        const yPrime = ry / Z;
        const sx = xPrime * screenScale + cx;
        const sy = yPrime * screenScale + cy + (pan.y * 0.5);
        const depthMetric = screenScale / Z;

        points.push({ x: sx, y: sy, z: rz, depth: depthMetric });
      }
    }

    // Draw mesh
    ctx.lineWidth = 1;

    for (let z = 0; z < gridSize - 1; z++) {
      for (let x = 0; x < gridSize - 1; x++) {
        const i = z * gridSize + x;
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + gridSize + 1];
        const p4 = points[i + gridSize];

        if (!p1 || !p2 || !p3 || !p4) continue;

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.lineTo(p3.x, p3.y);
        ctx.lineTo(p4.x, p4.y);
        ctx.closePath();

        // Fluid color grading
        const heightFactor = Math.min(1, Math.abs(p1.y - cy) / 150);
        const r = 40 + (heightFactor * 100);
        const g = 10 + (heightFactor * 200);
        const b = 60 + (heightFactor * 255);
        const alpha = 0.4 + (p1.depth * 0.6);

        ctx.fillStyle = `rgba(${r},${g},${b},${alpha * 0.8})`;
        ctx.strokeStyle = `rgba(${r + 50},${g + 50},${b + 100},${alpha * 0.3})`;

        ctx.fill();
        ctx.stroke();
      }
    }

    return newAutoRot;
  }
}
